import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PaymentIcon from "@mui/icons-material/Payment";
import UndoIcon from "@mui/icons-material/Undo";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import BlockIcon from "@mui/icons-material/Block";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import UnsubscribeIcon from "@mui/icons-material/Unsubscribe";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";

import { getOrderEvents } from "api/apiService";

const EVENT_META = {
  order_created:         { icon: AddCircleOutlineIcon,    color: "success", label: "Ordre opprettet" },
  order_edited:          { icon: EditIcon,                color: "info",    label: "Ordre redigert" },
  status_changed:        { icon: SwapHorizIcon,           color: "info",    label: "Status endret" },
  wc_status_changed:     { icon: SwapHorizIcon,           color: "secondary", label: "WC-status endret" },
  price_changed:         { icon: LocalAtmIcon,            color: "warning", label: "Pris endret" },
  delivery_date_changed: { icon: EventNoteIcon,           color: "warning", label: "Leveransedato endret" },
  date_change_requested: { icon: EventNoteIcon,           color: "info",    label: "Vendor ba om ny dato" },
  date_change_denied:    { icon: BlockIcon,               color: "error",   label: "Datoendring avslått" },
  delivery_deleted:      { icon: DeleteIcon,              color: "error",   label: "Leveranse slettet" },
  vendor_changed:        { icon: LocalShippingIcon,       color: "info",    label: "Vendor endret" },
  vendor_cancelled:      { icon: CancelScheduleSendIcon,  color: "warning", label: "Vendor avbestilte" },
  project_approved:      { icon: ThumbUpIcon,             color: "success", label: "Prosjekt godkjent" },
  payment_received:      { icon: PaymentIcon,             color: "success", label: "Betaling mottatt" },
  order_refunded:        { icon: UndoIcon,                color: "error",   label: "Refundert" },
  order_note_added:      { icon: NotificationsActiveIcon, color: "info",    label: "Notat lagt til" },
  wc_order_created:      { icon: CloudDoneIcon,           color: "info",    label: "Synket til WooCommerce" },
  wc_push_failed:        { icon: CloudOffIcon,            color: "error",   label: "WC-synk feilet" },
  wc_push_skipped:       { icon: EventBusyIcon,           color: "secondary", label: "WC-push hoppet over" },
  email_sent:            { icon: MarkEmailReadIcon,       color: "success", label: "E-post sendt" },
  email_skipped:         { icon: UnsubscribeIcon,         color: "secondary", label: "E-post hoppet over" },
  email_failed:          { icon: ErrorOutlineIcon,        color: "error",   label: "E-post feilet" },
};

// human-readable reasons for email_skipped
const SKIP_REASON_LABEL = {
  template_not_found:                   "Ingen mal konfigurert",
  "Customer email sending is disabled": "E-post deaktivert globalt",
  smtp_settings_missing:                "SMTP ikke konfigurert",
  orderId_required:                     "Mangler ordre-ID",
  order_not_found:                      "Ordre ikke funnet",
  awaiting_pay_url:                     "Venter på WC betalings-URL",
};

// human-readable email types
const EMAIL_TYPE_LABEL = {
  status_change:          "Statusendring",
  price_change_by_admin:  "Prisendring",
  delivery_date_changed:  "Leveransedato endret",
  payment_received:       "Betaling mottatt",
  order_refunded:         "Refundert",
  order_note_added:       "Ordrenotat",
  order_creation:         "Ordreoppretting",
  manual:                 "Manuell",
};

const SOURCE_LABEL = {
  bmax_admin: "Bmax admin",
  wc_webhook: "WooCommerce",
  customer:   "Kunde",
  vendor:     "Vendor",
  cron:       "System (cron)",
  system:     "System",
  schedule:   "Tidsplan",
};

function metaFor(eventType) {
  return EVENT_META[eventType] || { icon: InfoOutlinedIcon, color: "secondary", label: eventType };
}

/**
 * Build the "by …" label for an event. Prefers the hydrated actorName +
 * actorEmail combo, falls back to whatever was stashed in actorLabel for
 * legacy rows. Returns "" if we have nothing useful to show.
 */
function actorDisplay(event) {
  const name = event.actorName ? String(event.actorName).trim() : "";
  const email = event.actorEmail ? String(event.actorEmail).trim() : "";
  if (name && email) return `${name} (${email})`;
  if (name) return name;
  if (email) return email;
  if (event.actorLabel) return String(event.actorLabel);
  return "";
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("nb-NO", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function summarizeChange(event) {
  const { eventType, oldValue, newValue } = event;
  if (eventType === "status_changed") {
    const oldS = oldValue?.status || "—";
    const newS = newValue?.label || newValue?.status || "—";
    return `${oldS} → ${newS}`;
  }
  if (eventType === "wc_status_changed") {
    const oldS = oldValue?.wc_status || "—";
    const newS = newValue?.wc_status || "—";
    const mapped = newValue?.mappedBmaxStatus ? ` (mappes til ${newValue.mappedBmaxStatus})` : "";
    return `WC: ${oldS} → ${newS}${mapped}`;
  }
  if (eventType === "delivery_date_changed") {
    const d = newValue?.date || "?";
    const t = newValue?.time ? ` kl. ${newValue.time}` : "";
    return `Ny dato: ${d}${t}`;
  }
  if (eventType === "date_change_denied") {
    return `Forespørsel om ${newValue?.requestedDate || "?"} avslått`;
  }
  if (eventType === "wc_order_created") {
    return newValue?.wc_order_id ? `WC-ordre #${newValue.wc_order_id}` : "WC-ordre opprettet";
  }
  if (eventType === "wc_push_failed") {
    return newValue?.error ? String(newValue.error).slice(0, 120) : "Feil ved synk";
  }
  if (eventType === "order_created") {
    const parts = [];
    if (newValue?.grandTotal) parts.push(`Totalt: ${newValue.grandTotal} kr`);
    if (newValue?.pricingDeferred) parts.push("pris ikke satt");
    if (newValue?.onBehalfOfCustomer) parts.push("på vegne av kunden");
    return parts.join(" · ");
  }
  if (eventType === "order_edited") {
    return newValue?.grandTotal ? `Ny total: ${newValue.grandTotal} kr` : "Ordre oppdatert";
  }
  if (eventType === "price_changed") {
    const o = oldValue?.grandTotal;
    const n = newValue?.grandTotal;
    if (o != null && n != null) return `${o} kr → ${n} kr`;
    if (n != null) return `Ny total: ${n} kr`;
    return "Pris justert";
  }
  if (eventType === "delivery_deleted") {
    return `Slettet leveranse #${oldValue?.deliveryId || "?"}`;
  }
  if (eventType === "vendor_changed") {
    return newValue?.vendor_address
      ? `Ny vendor: ${newValue.vendor_address}`
      : `Vendor endret til #${newValue?.vendorId || "?"}`;
  }
  if (eventType === "vendor_cancelled") {
    return `Reassignet til vendor #${newValue?.reassignedToVendorId || "?"}`;
  }
  if (eventType === "project_approved") {
    return newValue?.vendorName ? `Godkjent av ${newValue.vendorName}` : "Godkjent";
  }
  if (eventType === "date_change_requested") {
    return `Ønsket: ${newValue?.requestedDate || "?"}${
      newValue?.requestedTime ? ` kl. ${newValue.requestedTime}` : ""
    }`;
  }
  if (eventType === "wc_push_skipped") {
    return newValue?.reason === "order_total_zero"
      ? "Hoppet over (total = 0)"
      : `Hoppet over: ${newValue?.reason || "ukjent"}`;
  }
  if (eventType === "email_sent") {
    const typeLabel = EMAIL_TYPE_LABEL[newValue?.emailType] || newValue?.emailType || "ukjent";
    // Statusdialogen loggførte mottakerne som rene adresser («a@b.no»), ikke
    // som { email, status }. De ble da regnet som feilet og vist som «til — ·
    // 1 feilet», selv om e-posten gikk ut. En ren adresse er en levert e-post.
    const recipients = (Array.isArray(newValue?.recipients) ? newValue.recipients : []).map((r) =>
      typeof r === "string" ? { email: r, status: true } : r
    );
    const successes = recipients.filter((r) => r.status);
    const failures = recipients.filter((r) => !r.status);
    const toList = successes
      .map((r) => r.email || r.role)
      .filter(Boolean)
      .slice(0, 3)
      .join(", ");
    let out = `Mal: ${typeLabel} · til ${toList || "—"}`;
    if (successes.length > 3) out += ` (+${successes.length - 3} til)`;
    if (failures.length) out += ` · ${failures.length} feilet`;
    return out;
  }
  if (eventType === "email_skipped") {
    const typeLabel = EMAIL_TYPE_LABEL[newValue?.emailType] || newValue?.emailType || "ukjent";
    const reason = SKIP_REASON_LABEL[newValue?.reason] || newValue?.reason || "ukjent årsak";
    return `Mal: ${typeLabel} · ${reason}`;
  }
  if (eventType === "email_failed") {
    const typeLabel = EMAIL_TYPE_LABEL[newValue?.emailType] || newValue?.emailType || "ukjent";
    const reason = newValue?.reason || "ukjent feil";
    return `Mal: ${typeLabel} · ${String(reason).slice(0, 120)}`;
  }
  return "";
}

// Hvor ofte siden ser etter nye hendelser. Ett lite kall; ordren hentes bare
// på nytt når noe faktisk er endret.
const POLL_MS = 10000;

function OrderEventTimeline({ orderId, onChange, refreshKey }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Siden oppdaterer seg selv. Nesten alt som skjer med en ordre loggføres som
  // en hendelse (statusendring, e-post, svar fra produsent, betaling fra
  // WooCommerce), så en ny hendelse betyr at ordren er endret — av admin i en
  // annen fane, av produsenten på lenken, eller av automatikken. Før måtte man
  // laste siden på nytt for å se det.
  const latestId = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!orderId) return;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await getOrderEvents(orderId, { limit: 100, offset: 0 });
        if (res?.ok && Array.isArray(res.events)) {
          const newest = res.events.reduce((max, e) => Math.max(max, Number(e.id) || 0), 0);
          const changed = latestId.current !== null && newest !== latestId.current;
          latestId.current = newest;
          setEvents(res.events);
          setError(null);
          // Ikke ved første lasting: da har siden nettopp hentet ordren selv.
          if (changed) onChangeRef.current?.();
        } else if (!silent) {
          setError(res?.error || "Kunne ikke laste events");
        }
      } catch (e) {
        // En bakgrunnssjekk som feiler (nett borte et øyeblikk) skal ikke
        // bytte ut loggen med en feilmelding.
        if (!silent) setError(e?.message || "Nettverksfeil");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [orderId]
  );

  useEffect(() => {
    latestId.current = null;
    load();
  }, [load]);

  // Etter en handling på siden (refreshKey endres når ordren er hentet på nytt).
  useEffect(() => {
    if (latestId.current !== null) load({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Bakgrunnssjekk mens fanen er synlig, og med en gang man kommer tilbake til den.
  useEffect(() => {
    if (!orderId) return undefined;
    const check = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    const timer = setInterval(check, POLL_MS);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, [orderId, load]);

  const toggle = () => setExpanded((v) => !v);

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <SoftBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={toggle}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          mb: expanded ? 2 : 0,
          "&:hover .hendelseslogg-title": { opacity: 0.85 },
        }}
      >
        <SoftBox display="flex" alignItems="center" gap={1}>
          <SoftTypography
            className="hendelseslogg-title"
            variant="h6"
            fontWeight="bold"
            sx={{ transition: "opacity 120ms ease" }}
          >
            Hendelseslogg
          </SoftTypography>
          {events.length > 0 && (
            <Chip
              label={events.length}
              size="small"
              sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
            />
          )}
          {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </SoftBox>
        <IconButton
          size="small"
          aria-label={expanded ? "Lukk hendelseslogg" : "Åpne hendelseslogg"}
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 180ms ease",
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </SoftBox>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {error && (
          <SoftTypography variant="caption" color="error">
            {error}
          </SoftTypography>
        )}

        {!loading && !error && events.length === 0 && (
          <SoftTypography variant="body2" color="secondary">
            Ingen hendelser logget for denne ordren ennå.
          </SoftTypography>
        )}

        <SoftBox sx={{ position: "relative", pl: 2 }}>
        {events.map((ev, idx) => {
          const { icon: Icon, color, label } = metaFor(ev.eventType);
          const summary = summarizeChange(ev);
          const sourceLabel = SOURCE_LABEL[ev.source] || ev.source;
          return (
            <SoftBox
              key={ev.id}
              display="flex"
              alignItems="flex-start"
              sx={{
                position: "relative",
                color: "white",
                pb: idx === events.length - 1 ? 0 : 2.5,
                "&::before": idx === events.length - 1 ? undefined : {
                  content: '""',
                  position: "absolute",
                  left: 14,
                  top: 28,
                  bottom: 0,
                  width: "2px",
                  backgroundColor: (t) => t.palette.grey[300],
                },
              }}
            >
              <SoftBox
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  backgroundColor: (t) => t.palette[color]?.main || t.palette.grey[500],
                  color: "white",
                  zIndex: 1,
                }}
              >
                <Icon fontSize="small" />
              </SoftBox>
              <SoftBox ml={2} flexGrow={1}>
                <SoftBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                  <SoftTypography variant="button" fontWeight="bold">
                    {label}
                  </SoftTypography>
                  <Chip label={sourceLabel} size="small" variant="outlined" />
                </SoftBox>
                {summary && (
                  <SoftTypography variant="body2" color="text" sx={{ mt: 0.25 }}>
                    {summary}
                  </SoftTypography>
                )}
                <SoftBox display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                  <SoftTypography variant="caption" color="secondary">
                    {formatDateTime(ev.createdAt)}
                  </SoftTypography>
                  {(() => {
                    const who = actorDisplay(ev);
                    return who ? (
                      <SoftTypography variant="caption" color="secondary">
                        · av {who}
                      </SoftTypography>
                    ) : null;
                  })()}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          );
        })}
        </SoftBox>
      </Collapse>
    </Card>
  );
}

OrderEventTimeline.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // Kalles når en ny hendelse dukker opp, så siden kan hente ordren på nytt.
  onChange: PropTypes.func,
  // Endres av siden etter en handling; loggen hentes da på nytt med en gang.
  refreshKey: PropTypes.any,
};

export default OrderEventTimeline;
